/**
 * Trip allocation label — hover (desktop) or tap (mobile) for trip details.
 */

import React, { useMemo, useState } from "react";
import { Button, Drawer, Popover, Spin, Tag } from "antd";
import { CarOutlined, ExportOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useDeviceType } from "@/features/leads/hooks/useDeviceType.js";
import { getTrip } from "@/features/trips/services/tripService.js";
import { TRIP_STATUS_LABELS } from "@/features/trips/constants/tripStatuses.js";
import { formatCorridorDisplay } from "@/shared/utils/routeCorridorUtils.js";
import { logError } from "@/shared/utils/logger.js";

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px dashed ${(props) => (props.$unassigned ? "#d9d9d9" : "#91d5ff")};
  background: ${(props) => (props.$unassigned ? "#fafafa" : "#e6f7ff")};
  color: ${(props) => (props.$unassigned ? "#8c8c8c" : "#0958d9")};
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  max-width: 100%;
  line-height: 1.4;

  &:hover {
    border-color: ${(props) => (props.$unassigned ? "#bfbfbf" : "#69b1ff")};
  }
`;

const DetailBlock = styled.div`
  min-width: 240px;
  max-width: 320px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  margin-bottom: 8px;

  span:first-child {
    color: #8c8c8c;
  }

  span:last-child {
    color: #262626;
    font-weight: 500;
    text-align: right;
  }
`;

function formatDeparture(value) {
  if (!value) return "TBD";
  try {
    return format(new Date(value), "PPp");
  } catch {
    return String(value);
  }
}

function TripDetailsContent({ trip, orderCount, onOpenTrip, loading }) {
  if (loading) {
    return (
      <DetailBlock style={{ textAlign: "center", padding: 16 }}>
        <Spin size="small" />
      </DetailBlock>
    );
  }

  if (!trip) {
    return (
      <DetailBlock>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Unassigned</div>
        <div style={{ fontSize: 13, color: "#8c8c8c" }}>
          This delivery is not linked to a trip yet.
        </div>
      </DetailBlock>
    );
  }

  const corridor = formatCorridorDisplay(trip.routeCorridor) || trip.routeCorridor;
  const status =
    TRIP_STATUS_LABELS[trip.status] ||
    String(trip.status || "unknown").replaceAll("_", " ");

  return (
    <DetailBlock>
      <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>
        {trip.id}
      </div>
      <DetailRow>
        <span>Corridor</span>
        <span>{corridor || "—"}</span>
      </DetailRow>
      <DetailRow>
        <span>Departure</span>
        <span>{formatDeparture(trip.departureDate)}</span>
      </DetailRow>
      <DetailRow>
        <span>Status</span>
        <span>{status}</span>
      </DetailRow>
      <DetailRow>
        <span>Orders on trip</span>
        <span>{orderCount ?? trip.orders?.length ?? "—"}</span>
      </DetailRow>
      {trip.vehicleId ? (
        <DetailRow>
          <span>Vehicle</span>
          <span>{trip.vehicleId}</span>
        </DetailRow>
      ) : null}
      {trip.driverId ? (
        <DetailRow>
          <span>Driver</span>
          <span>{trip.driverId}</span>
        </DetailRow>
      ) : null}
      {onOpenTrip ? (
        <Button
          type="link"
          size="small"
          icon={<ExportOutlined />}
          onClick={onOpenTrip}
          style={{ paddingInline: 0, marginTop: 4 }}
        >
          Open trip
        </Button>
      ) : null}
    </DetailBlock>
  );
}

/**
 * @param {Object} props
 * @param {string|null|undefined} props.tripId
 * @param {Object|null|undefined} [props.trip]
 * @param {number} [props.orderCount]
 */
export default function TripLabelChip({ tripId, trip: tripProp, orderCount }) {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [loadedTrip, setLoadedTrip] = useState(null);
  const [loading, setLoading] = useState(false);

  const trip = tripProp || loadedTrip;
  const unassigned = !tripId;

  const label = useMemo(() => {
    if (unassigned) return "Unassigned";
    return tripId;
  }, [tripId, unassigned]);

  const ensureTripLoaded = async () => {
    if (unassigned || tripProp || loadedTrip) return;
    setLoading(true);
    try {
      const data = await getTrip(tripId);
      setLoadedTrip(data || null);
    } catch (error) {
      logError("TripLabelChip: failed to load trip", {
        tripId,
        error: error?.message,
      });
      setLoadedTrip(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTrip = (event) => {
    event?.stopPropagation?.();
    if (!tripId) return;
    navigate(`/trips/${tripId}`);
    setDrawerOpen(false);
    setPopoverOpen(false);
  };

  const handleActivate = async (event) => {
    event?.stopPropagation?.();
    await ensureTripLoaded();
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setPopoverOpen(true);
    }
  };

  const details = (
    <TripDetailsContent
      trip={trip}
      orderCount={orderCount}
      loading={loading}
      onOpenTrip={tripId ? handleOpenTrip : undefined}
    />
  );

  const unassignedChip = (
    <ChipButton type="button" $unassigned onClick={handleActivate}>
      {label}
    </ChipButton>
  );

  if (unassigned) {
    if (isMobile) {
      return (
        <>
          {unassignedChip}
          <Drawer
            title="Trip allocation"
            placement="bottom"
            height="auto"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            styles={{ body: { paddingBottom: 24 } }}
          >
            {details}
          </Drawer>
        </>
      );
    }

    return (
      <Popover
        content={details}
        trigger="hover"
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
      >
        {unassignedChip}
      </Popover>
    );
  }

  const chip = (
    <ChipButton type="button" $unassigned={false} onClick={handleActivate}>
      <CarOutlined style={{ fontSize: 12 }} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </ChipButton>
  );

  if (isMobile) {
    return (
      <>
        {chip}
        <Drawer
          title="Trip details"
          placement="bottom"
          height="auto"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          styles={{ body: { paddingBottom: 24 } }}
        >
          {details}
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      content={details}
      trigger="hover"
      open={popoverOpen}
      onOpenChange={(open) => {
        setPopoverOpen(open);
        if (open) ensureTripLoaded();
      }}
    >
      {chip}
    </Popover>
  );
}

/**
 * Compact corridor tag for list headers and flat columns.
 * @param {{ corridor: string|null|undefined }} props
 */
export function CorridorTag({ corridor }) {
  if (!corridor || corridor === "Unknown corridor") {
    return <Tag>Unknown</Tag>;
  }
  return <Tag color="blue">{corridor}</Tag>;
}
